import type {
  ReadOneOwnerBallot,
  CreateOneOwnerBallot,
  UpdateOneOwnerBallot,
} from '@sheridan-prize/api';
import axios from 'axios';
import { getAuthStore } from './main';

export type BallotCode =
  | 'NO_BALLOT'
  | 'BALLOT_SUBMITTED'
  | 'BALLOT_NOT_SUBMITTED'
  | 'ERR'
  | 'S0';

export interface BallotState {
  code: BallotCode;
  ballot: ReadOneOwnerBallot | null;
  error: unknown | null;
}

export class BallotStore {
  #state: BallotState;

  constructor() {
    this.#state = { code: 'S0', ballot: null, error: null };
  }

  async #readBallot(id: string): Promise<void> {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_BASE_URL}/v1/ballots/${id}`,
        { withCredentials: true },
      );
      this.#state = {
        code: 'BALLOT_NOT_SUBMITTED',
        ballot: response.data[0],
        error: null,
      };
    } catch (err) {
      if (
        axios.isAxiosError(err) &&
        err.response?.data.code === 'AlreadySubmitted'
      ) {
        this.#state = { code: 'BALLOT_SUBMITTED', ballot: null, error: null };
      } else if (
        axios.isAxiosError(err) &&
        Number.parseInt(err.code, 10) === 404
      ) {
        this.#state = { code: 'NO_BALLOT', ballot: null, error: null };
      } else {
        this.#state = { code: 'ERR', ballot: null, error: err };
      }
    }
  }

  get state(): BallotState {
    return this.#state;
  }

  async init(): Promise<void> {
    try {
      const [userDoc, activeContest] = await Promise.all([
        getAuthStore().getUserDoc(),
        axios.get(`${import.meta.env.VITE_API_BASE_URL}/v1/active-contest`, {
          withCredentials: true,
        }),
      ]);

      const ballotObj = userDoc.ballots.find(
        (ballot) => ballot.contest === activeContest.data[0]._id,
      );

      if (!ballotObj) {
        this.#state = { code: 'NO_BALLOT', ballot: null, error: null };
      } else {
        await this.#readBallot(ballotObj._id);
      }
    } catch (err) {
      if (
        axios.isAxiosError(err) &&
        err.response?.data.code === 'AlreadySubmitted'
      ) {
        this.#state = { code: 'BALLOT_SUBMITTED', ballot: null, error: null };
      } else if (
        axios.isAxiosError(err) &&
        err.response?.data?.errors.existingBallotId
      ) {
        await this.#readBallot(err.response.data.errors.existingBallotId);
      } else {
        this.#state = { code: 'ERR', ballot: null, error: err };
      }
    }
  }

  async createOrUpdateBallot(
    data: CreateOneOwnerBallot | UpdateOneOwnerBallot,
  ): Promise<void> {
    try {
      let createResponse: ReadOneOwnerBallot | undefined;

      if (this.#state.code === 'NO_BALLOT') {
        const response = await axios.post(
          `${import.meta.env.VITE_API_BASE_URL}/v1/ballots`,
          data,
          { withCredentials: true },
        );
        createResponse = response.data[0];
      } else if (this.#state.code === 'BALLOT_NOT_SUBMITTED') {
        await axios.put(
          `${import.meta.env.VITE_API_BASE_URL}/v1/ballots/${this.#state.ballot._id}`,
          data,
          { withCredentials: true },
        );
      }

      if (!data.isSubmitted && createResponse) {
        this.#state = {
          code: 'BALLOT_NOT_SUBMITTED',
          ballot: createResponse,
          error: null,
        };
      } else if (!data.isSubmitted && !createResponse) {
        const mergedBallot = JSON.parse(JSON.stringify(this.#state.ballot));
        mergedBallot.isSubmitted = data.isSubmitted;
        mergedBallot.choices = JSON.parse(JSON.stringify(data.choices));

        this.#state = {
          code: 'BALLOT_NOT_SUBMITTED',
          ballot: mergedBallot,
          error: null,
        };
      } else if (data.isSubmitted) {
        this.#state = { code: 'BALLOT_SUBMITTED', ballot: null, error: null };
      }
    } catch (err) {
      if (
        axios.isAxiosError(err) &&
        err.response?.data.code === 'AlreadySubmitted'
      ) {
        this.#state = { code: 'BALLOT_SUBMITTED', ballot: null, error: null };
      } else if (
        axios.isAxiosError(err) &&
        err.response?.data?.errors.existingBallotId
      ) {
        await this.#readBallot(err.response.data.errors.existingBallotId);
      } else {
        this.#state = { code: 'ERR', ballot: null, error: err };
      }
    }
  }
}
